import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Database, Loader2, CheckCircle, XCircle, AlertTriangle, Play, RefreshCw,
} from "lucide-react";
import { Button } from "./ui/button";

/**
 * BM-H42 — Cross-user backfill status tile.
 *
 * The cross-user category backfill (~10.7k filings) runs as a durable,
 * self-driving background job: a per-worker driver re-kicks it from a
 * persisted cursor whenever a worker recycle kills the detached task, so it
 * finishes on its own with NO browser-console babysitting. This tile is the
 * window onto that job — it polls the shared status doc so Vanessa can watch
 * "processed / total" climb and see at a glance whether it's live, stalled
 * (the driver will resume it), done, or errored.
 *
 * Props:
 *   apiBase    — API origin (e.g. the staging/prod azure host)
 *   getHeaders — () => ({...}) returning the X-Bonsai-User-Id auth headers
 */
const STATUS_PATH = "/api/bonsai/filing/admin/backfill-status";
const START_PATH =
  "/api/bonsai/filing/admin/backfill-cross-user-categories?days=365&dry_run=false";
const POLL_MS = 5000;
const STALE_MINUTES = 15; // mirrors backend _BACKFILL_STALE_MINUTES

function pct(processed, total) {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.round((processed / total) * 100));
}

function minutesSince(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 60000;
}

export default function BackfillStatusTile({ apiBase, getHeaders }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${apiBase}${STATUS_PATH}`, {
        method: "GET",
        headers: getHeaders(),
      });
      if (!resp.ok) throw new Error(`status ${resp.status}`);
      const data = await resp.json();
      setDoc(data);
      setErr("");
    } catch (e) {
      setErr(e.message || "Could not load backfill status");
    } finally {
      setLoading(false);
    }
  }, [apiBase, getHeaders]);

  useEffect(() => {
    fetchStatus();
    timer.current = setInterval(fetchStatus, POLL_MS);
    return () => timer.current && clearInterval(timer.current);
  }, [fetchStatus]);

  const startRun = async () => {
    setStarting(true);
    try {
      await fetch(`${apiBase}${START_PATH}`, {
        method: "POST",
        headers: getHeaders(),
      });
      // 202 (started) or 409 (already running) are both fine — either way a
      // run now holds the slot; just refresh the view.
      await fetchStatus();
    } catch (e) {
      setErr(e.message || "Could not start backfill");
    } finally {
      setStarting(false);
    }
  };

  const status = doc?.status || "idle";
  const processed = Number(doc?.filings_processed || 0);
  const total = doc?.filings_total;
  const percent = pct(processed, total);
  const idleMin = minutesSince(doc?.last_progress_at);
  const isStalled =
    status === "running" && idleMin != null && idleMin >= STALE_MINUTES;

  // Visual state
  let Badge = Database;
  let badgeClass = "text-txt-muted";
  let label = "Idle";
  if (status === "running" && !isStalled) {
    Badge = Loader2;
    badgeClass = "text-teal animate-spin";
    label = "Running";
  } else if (isStalled) {
    Badge = AlertTriangle;
    badgeClass = "text-amber-400";
    label = "Stalled — self-resuming";
  } else if (status === "done") {
    Badge = CheckCircle;
    badgeClass = "text-emerald-400";
    label = "Complete";
  } else if (status === "error") {
    Badge = XCircle;
    badgeClass = "text-red-400";
    label = "Error";
  }

  return (
    <section className="glass-card p-6 rounded-md" data-testid="backfill-status-tile">
      <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
        <Database className="w-4 h-4" /> Cross-User Category Backfill
      </h2>

      <div className="flex items-center gap-2 mb-3">
        <Badge className={`w-4 h-4 ${badgeClass}`} />
        <span className="text-sm font-medium text-txt" data-testid="backfill-status-label">
          {label}
        </span>
        {status === "running" && idleMin != null && (
          <span className="text-xs text-txt-muted">
            · last progress {idleMin < 1 ? "just now" : `${Math.floor(idleMin)}m ago`}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 rounded-full bg-navy-hover overflow-hidden mb-2">
        <div
          className={`h-full transition-all duration-500 ${
            status === "error"
              ? "bg-red-400"
              : status === "done"
              ? "bg-emerald-400"
              : isStalled
              ? "bg-amber-400"
              : "bg-teal"
          }`}
          style={{ width: `${percent}%` }}
          data-testid="backfill-progress-bar"
        />
      </div>
      <p className="text-xs text-txt-muted mb-3" data-testid="backfill-progress-text">
        {processed.toLocaleString()}
        {total != null ? ` / ${Number(total).toLocaleString()}` : ""} filings
        {total != null ? ` (${percent}%)` : ""}
        {Number(doc?.timed_out || 0) > 0 && ` · ${doc.timed_out} timed out`}
        {Number(doc?.errors || 0) > 0 && ` · ${doc.errors} errors`}
      </p>

      {isStalled && (
        <p className="text-xs text-amber-400/90 mb-3 flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          This run stalled (its worker recycled). The self-driving loop will
          pick it up from the last cursor within a couple of minutes — no action
          needed. Use the button below only if you want to force it now.
        </p>
      )}

      {status === "error" && Array.isArray(doc?.error_sample) && doc.error_sample.length > 0 && (
        <details className="text-xs text-txt-muted mb-3">
          <summary className="cursor-pointer">Error sample ({doc.error_sample.length})</summary>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            {doc.error_sample.slice(0, 10).map((e, i) => (
              <li key={i} className="break-all">{String(e)}</li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={startRun}
          disabled={starting || (status === "running" && !isStalled)}
          data-testid="backfill-start-btn"
          className="border-navy-border text-txt hover:bg-navy-hover"
        >
          {starting ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          ) : (
            <Play className="w-3.5 h-3.5 mr-1" />
          )}
          {status === "running" && !isStalled
            ? "Running…"
            : isStalled
            ? "Force resume now"
            : status === "done"
            ? "Run again"
            : "Start backfill"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchStatus}
          data-testid="backfill-refresh-btn"
          className="text-txt-muted hover:text-txt"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {err && (
        <p className="text-xs text-red-400 mt-2" data-testid="backfill-error">
          {err}
        </p>
      )}
      {loading && !doc && (
        <p className="text-xs text-txt-muted mt-2">Loading status…</p>
      )}
    </section>
  );
}
